<template>
  <registration-layout hide-nav class="user-registration-complete" size="tiny">
    <loading-placeholder v-if="isLoading" :lines="10" />
    <template v-else-if="userRegistered">
      <h3>Welcome, {{ formData.name }}! 👋</h3>
      <p>You're ready to go!<br>Ready to start working with the rest of your team?</p>
      <p><br></p>
      <!-- Explicit link click to ensure full page reload and Appcues hydration -->
      <dd-button color="blue" @click="RouteAssistant.goToUrl(RouteAssistant.getAccountPickerUrl())">Join your team</dd-button>
    </template>
    <template v-else-if="hasTokenErrorMessage">
      <h3>Complete your registration</h3>
      <p v-html="tokenErrorMessage" />
    </template>
    <template v-else>
      <h3>Complete your registration</h3>
      <el-form ref="form" :model="formData" :rules="formRules" label-position="top">
        <el-form-item label="Full Name" prop="name">
          <el-input ref="nameRef" v-model="formData.name" placeholder="Enter your full name" maxlength="100" />
        </el-form-item>
        <template v-if="!completeUserData.viaGoogleAuth">
          <el-form-item label="Password" class="short-bottom" prop="password">
            <el-input v-model="formData.password" type="password" placeholder="Enter a password (minimum 8 chars)" maxlength="100" />
          </el-form-item>
          <el-form-item prop="passwordConfirm">
            <el-input v-model="formData.passwordConfirm" type="password" placeholder="Re-enter Password" maxlength="100" />
          </el-form-item>
        </template>
        <el-form-item label="Time Zone" prop="timezone">
          <single-select v-model="formData.timezone" placeholder="Choose a time zone...">
            <el-option v-for="tz in completeUserData.listTimeZones" :key="tz.name" :value="tz.name" :label="tz.value" />
          </single-select>
        </el-form-item>
        <el-form-item>
          <dd-checkbox v-model="formData.acceptTerms" dd-size="medium">
            I agree to the <a href="https://www.donedone.com/terms-of-use" class="just-a-link" target="_blank">Terms of Use</a> *
          </dd-checkbox>
          <dd-checkbox v-model="formData.acceptEmailList" dd-size="medium">
            Subscribe to news and updates
          </dd-checkbox>
        </el-form-item>
      </el-form>
      <dd-button
        :is-disabled="disableSubmit"
        :is-loading="isSubmitting"
        color="blue"
        @click="submitForm()"
      >Register</dd-button>
    </template>
  </registration-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import RegistrationLayout from '@/layouts/registration-layout.vue'
import MainContainer from '@/layouts/main-container.vue'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import TimeAssistant from '@/helpers/time-assistant'
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { IRegistrationOutputCompleteUser } from '@/api/types/response.types'

/* API */
import RegistrationAPI from '@/api/modules/registration.api'
import { IRegistrationInputCompleteUser } from '@/api/types/request.types'

export default defineComponent({
  name: 'UserRegistrationComplete',

  components: {
    RegistrationLayout,
    MainContainer,
  },

  data() {
    return {
      RouteAssistant,
      tokenErrorMessage: '',
      userRegistered: false,
      isLoading: true,
      isSubmitting: false,
      completeUserData: {} as IRegistrationOutputCompleteUser,
      formData: {
        encryptedUserToken: '',
        name: '',
        password: '',
        passwordConfirm: '',
        timezone: '',
        viaGoogleAuth: false,
        acceptTerms: false,
        acceptEmailList: false,
      } as IRegistrationInputCompleteUser,
      formRules: {
        name: [
          {
            validator: (rule: any, value: any, callback: any) => {
              if (value && value.trim() !== '') {
                callback()
              } else {
                callback(new Error('Name is required'))
              }
            },
            trigger: 'blur',
          },
        ],
        password: [
          {
            validator: (rule: any, value: any, callback: any) => {
              // @ts-ignore
              if (this.completeUserData.viaGoogleAuth) {
                callback()
              }

              if (value.length < 8) {
                callback(new Error('Password must be at least 8 characters'))
              } else if (!/\d/.test(value)) {
                callback(new Error('Password must contain a digit (0-9)'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        passwordConfirm: [
          {
            validator: (rule: any, value: any, callback: any) => {
              // @ts-ignore
              if (this.completeUserData.viaGoogleAuth) {
                callback()
              }
              // @ts-ignore
              if (value !== this.formData.password) {
                callback(new Error('Password must match'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        timezone: [{ required: true, message: 'Timezone is required' }],
      },
    }
  },

  head(): any {
    return {
      title: 'Complete user registration',
    }
  },

  computed: {
    disableSubmit(): boolean {
      return !this.formData.acceptTerms
    },
    hasTokenErrorMessage(): boolean {
      return (
        this.tokenErrorMessage !== undefined &&
        this.tokenErrorMessage.length > 0
      )
    },
  },

  async created() {
    this.isLoading = true
    await this.getCompleteUserData()
    this.isLoading = false
  },

  methods: {
    async submitForm() {
      // @ts-ignore
      this.$refs.form.validate(valid => {
        if (valid) {
          this.completeUser()
        }
      })
    },

    async completeUser() {
      this.isSubmitting = true
      try {
        await RegistrationAPI.post_CompleteUser(this.formData)
        this.userRegistered = true
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },

    async getCompleteUserData() {
      try {
        const viaGoogleAuth = this.$route.query.via_google_auth === 'true'
        this.completeUserData = await RegistrationAPI.get_CompleteUser(
          viaGoogleAuth,
          TextAssistant.urlTokenCleaner(this.$route.query.token as string),
        )
        this.formData.timezone = TimeAssistant.guessTimezoneID()
        this.formData.viaGoogleAuth = this.completeUserData.viaGoogleAuth
        this.formData.encryptedUserToken = this.completeUserData.encryptedUserToken
      } catch (error) {
        this.tokenErrorMessage = error.data.message
      }
    },
  },
})
</script>

<style lang="scss">
.user-registration-complete {
  h3 {
    text-align: center;
  }
  p {
    text-align: center;
    > a {
      @include lettering--normal-link;
    }
  }
  .el-form-item {
    &.short-bottom {
      margin-bottom: 12px;
    }
  }
  .el-checkbox {
    margin-bottom: 0;
    font-weight: normal;
  }
}
</style>
